'use client'
import { memo, useEffect, useMemo, useState } from 'react'

type ReviewsSummary = {
  '5_rating'?: number
  '4_rating'?: number
  '3_rating'?: number
  '2_rating'?: number
  '1_rating'?: number
}

interface RatingData {
  star: number
  count: number
  percentage: number
}

function RatingsBarBaseUpdated({ reviews = {} as ReviewsSummary }) {
  const ratings = useMemo<RatingData[]>(() => {
    const ratingCounts: Record<1 | 2 | 3 | 4 | 5, number> = {
      5: reviews['5_rating'] ?? 0,
      4: reviews['4_rating'] ?? 0,
      3: reviews['3_rating'] ?? 0,
      2: reviews['2_rating'] ?? 0,
      1: reviews['1_rating'] ?? 0,
    }

    const maxCount = Math.max(...Object.values(ratingCounts))

    return [5, 4, 3, 2, 1].map((star) => ({
      star,
      count: ratingCounts[star as keyof typeof ratingCounts],
      percentage: maxCount > 0 ? ratingCounts[star as keyof typeof ratingCounts] / maxCount : 0,
    }))
  }, [reviews])

  const [animatedValues, setAnimatedValues] = useState<number[]>(
    new Array(ratings.length).fill(0),
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValues(ratings.map((r) => r.percentage * 100))
    }, 100)

    return () => clearTimeout(timer)
  }, [ratings])

  return (
    <div className='md:w-[325px] w-[300px] space-y-[5.03px] p-1 md:p-0'>
      {ratings.map((r, index) => (
        <div key={r.star} className='flex items-center gap-4'>
          {/* Star */}
          <span className='w-[15.32px] font-medium text-[16.57px] text-[#1E1F24]'>
            {r.star}
          </span>

          {/* Progress */}
          <progress
            max={100}
            value={animatedValues[index]}
            className='rating-progress'
          />

          {/* Count */}
          <span className='min-w-[24px] text-[13px] text-[#191919]'>
            {r.count}
          </span>
        </div>
      ))}
    </div>
  )
}

export default memo(RatingsBarBaseUpdated)
